import React from 'react';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import { SecondaryBody } from '@grnhse/seedling/lib/birch/typography';
import { FullPalette } from '@grnhse/seedling/lib/birch/colors';
import styled from 'styled-components';
import CandidateChips from 'talent_matching/components/shared/candidate_chips';
import CompanyTitleField from './company_title_field';
import SocialLinks from 'talent_matching/components/shared/social_links';
import Bookmark from 'talent_matching/components/shared/bookmark';
import { Row } from 'shared/components/birch/row';
import { type Person, Row as PersonRow } from 'talent_matching/types';
import { FraudIcon } from 'fraud/fraud_icon';
import { useUserContext } from 'talent_matching/contexts/user_context';
import { RejectModalProvider } from '../../../application_review/components/reject_modal_context';
import { withLocalePrefix } from 'shared/utils/translation';

const t = withLocalePrefix('talent_matching');

const MetadataText = styled.span`
  color: ${FullPalette.GREY_900};
`;

const PrimaryText = styled.span`
  color: ${FullPalette.EVERGREEN_700};
`;

type Props = {
  candidate: Person;
  isRowSelected: boolean;
  isInSidepanel: boolean;
  onRejectSuccess: (row: PersonRow, closeModal: () => void) => void;
};

const CandidateOverviewCell = ({
  candidate,
  isRowSelected,
  isInSidepanel,
  onRejectSuccess,
}: Props) => {
  const { canUseLocations } = useUserContext();

  const {
    title,
    company,
    latestEmployment,
    referral,
    internal,
    activeOnAnotherJob,
    potentialDuplicate,
    dreamJob,
    linkedinAddress,
    githubAddress,
    websiteAddress,
    resume,
    previouslyRejectedFromJob,
    isBookmarked,
    fullName,
    fraudReportHighRisk,
  } = candidate;

  const resumeAddress = resume?.details?.address;
  const { canViewFraudReports } = useUserContext();

  const showFraudIcon = canViewFraudReports && fraudReportHighRisk;

  const resumeEmployment = resume?.details?.employments?.[0];

  const showCompanyTitle =
    title || company || latestEmployment || resumeEmployment;
  const showCandidateChips =
    referral ||
    internal ||
    activeOnAnotherJob ||
    potentialDuplicate ||
    dreamJob ||
    previouslyRejectedFromJob;
  const showSocialLinks =
    linkedinAddress || Boolean(resume?.url) || githubAddress || websiteAddress;
  const showBookmark = !!isBookmarked || isRowSelected || isInSidepanel;

  return (
    <Stack
      spacing={1}
      alignContent="flex-start"
      sx={{
        padding: 1,
        paddingLeft: 0,
        height: '100%',
      }}
    >
      <Row spacing={0.5}>
        <SecondaryBody medium className="candidate-full-name pii-blur">
          {fullName}
        </SecondaryBody>
        {showFraudIcon && <FraudIcon size="sm" />}
        <Stack sx={{ marginTop: -0.5 }}>
          <Bookmark
            className={showBookmark ? '' : 'hide'}
            application={candidate}
            size="seedling-only-sm"
          />
        </Stack>
      </Row>
      {showCompanyTitle && (
        <CompanyTitleField
          title={title}
          company={company}
          latestEmployment={latestEmployment}
          resumeEmployment={resumeEmployment}
        />
      )}
      {canUseLocations && resumeAddress && (
        <SecondaryBody>
          <MetadataText>{t('table.labels.address_label')}</MetadataText>
          <PrimaryText className="pii-blur">{resumeAddress}</PrimaryText>
        </SecondaryBody>
      )}
      {showCandidateChips && <CandidateChips application={candidate} />}
      {showSocialLinks && (
        <RejectModalProvider>
          <SocialLinks
            application={candidate}
            onRejectSuccess={onRejectSuccess}
            isFraud={showFraudIcon}
          />
        </RejectModalProvider>
      )}
    </Stack>
  );
};

export default CandidateOverviewCell;
